import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { paymentGatewayApi } from '@/services/paymentGatewayApi';
import { useQuery, useMutation as useReactQueryMutation } from '@tanstack/react-query';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, RefreshCw, CreditCard, CheckCircle2, XCircle, Edit } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { PaymentGateway, GatewayMetrics, GatewayCacheStatus, PaymentGatewayConfig } from '@/types/paymentGateway';

const GATEWAY_NAMES: Record<PaymentGateway, string> = {
  MOCK: 'Mock Gateway',
  STRIPE: 'Stripe',
  PAYPAL: 'PayPal',
  RAZORPAY: 'Razorpay',
  SQUARE: 'Square',
  ADYEN: 'Adyen',
  BRAINTREE: 'Braintree',
  CHECKOUT_COM: 'Checkout.com',
  WORLDPAY: 'Worldpay',
  PAYU: 'PayU',
  MOLLIE: 'Mollie',
  KLARNA: 'Klarna',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  AMAZON_PAY: 'Amazon Pay',
  ALIPAY: 'Alipay',
  WECHAT_PAY: 'WeChat Pay',
};

export const PaymentGatewayDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const configId = id;
  const { hasPermission } = useAdminAuth();

  const canView = hasPermission('view', 'paymentGateway');
  const canEdit = hasPermission('edit', 'paymentGateway');
  const canRefresh = hasPermission('update', 'paymentGateway');

  // Fetch gateway configuration by ID
  const {
    data: gatewayConfig,
    isLoading: isLoadingConfig,
    error: configError,
  } = useQuery<PaymentGatewayConfig>({
    queryKey: ['paymentGateway', configId, 'config'],
    queryFn: () => paymentGatewayApi.getConfigById(configId!),
    enabled: canView && !!configId,
  });

  // Extract gateway code from config
  const gatewayCode = gatewayConfig?.gatewayType as PaymentGateway | undefined;

  // Fetch gateway status
  const {
    data: gatewayStatuses,
    isLoading: isLoadingStatus,
    error: statusError,
  } = useQuery({
    queryKey: ['paymentGateways', 'status'],
    queryFn: () => paymentGatewayApi.getAvailableGateways(),
    enabled: canView && !!gatewayCode,
  });

  // Fetch gateway metrics
  const {
    data: metrics,
    isLoading: isLoadingMetrics,
    error: metricsError,
    refetch: refetchMetrics,
  } = useQuery<GatewayMetrics>({
    queryKey: ['paymentGateway', gatewayCode, 'metrics'],
    queryFn: () => paymentGatewayApi.getMetrics(gatewayCode!),
    enabled: canView && !!gatewayCode,
  });

  // Fetch cache status
  const {
    data: cacheStatus,
    isLoading: isLoadingCache,
    error: cacheError,
    refetch: refetchCache,
  } = useQuery<GatewayCacheStatus>({
    queryKey: ['paymentGateway', gatewayCode, 'cache'],
    queryFn: () => paymentGatewayApi.getCacheStatus(gatewayCode!),
    enabled: canView && !!gatewayCode,
  });

  // Refresh cache mutation
  const refreshCacheMutation = useReactQueryMutation({
    mutationFn: async () => {
      if (gatewayCode) {
        await paymentGatewayApi.refreshCache(gatewayCode);
      }
    },
    onSuccess: () => {
      refetchCache();
      refetchMetrics();
    },
  });

  const isLoading = isLoadingStatus || isLoadingMetrics || isLoadingCache || isLoadingConfig;
  const error = statusError || metricsError || cacheError || configError;

  const gatewayEnabled = gatewayStatuses?.[gatewayCode!] ?? false;
  const gatewayName = gatewayConfig?.gatewayName || (gatewayCode ? GATEWAY_NAMES[gatewayCode] : undefined) || configId;

  const handleRefreshCache = async () => {
    if (!window.confirm(`Refresh cache for ${gatewayName}?`)) return;
    await refreshCacheMutation.mutateAsync();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  if (isLoading) {
    return <LoadingState message="Loading payment gateway details..." />;
  }

  if (error || !gatewayConfig || !configId) {
    return <ErrorState error={error || new Error('Payment gateway not found')} onRetry={() => window.location.reload()} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/payment-gateways">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              {gatewayName}
            </h2>
            <p className="text-muted-foreground mt-1">
              {gatewayCode && `Gateway Code: ${gatewayCode}`}
              {!gatewayCode && `Config ID: ${configId}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && gatewayConfig?.id && (
            <Button asChild>
              <Link to={`/admin/payment-gateways/${gatewayConfig.id}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit Configuration
              </Link>
            </Button>
          )}
          {!gatewayConfig && canEdit && (
            <Button asChild variant="outline">
              <Link to={`/admin/payment-gateways/new?gateway=${gatewayCode}`}>
                <Edit className="mr-2 h-4 w-4" />
                Create Configuration
              </Link>
            </Button>
          )}
          {canRefresh && (
            <Button variant="outline" onClick={handleRefreshCache} disabled={refreshCacheMutation.isPending}>
              <RefreshCw className={`mr-2 h-4 w-4 ${refreshCacheMutation.isPending ? 'animate-spin' : ''}`} />
              Refresh Cache
            </Button>
          )}
        </div>
      </div>

      {/* Status Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Label>Status:</Label>
              <div className="flex items-center gap-2">
                {gatewayEnabled ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      Enabled
                    </span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-gray-400" />
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
                      Disabled
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="metrics">Metrics</TabsTrigger>
          <TabsTrigger value="cache">Cache</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Gateway Information</CardTitle>
              <CardDescription>Basic gateway details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Gateway Name</Label>
                  <Input value={gatewayName} disabled />
                </div>
                {gatewayCode && (
                  <CopyableId id={gatewayCode} label="Gateway Code" />
                )}
              </div>
              <CopyableId id={configId} label="Config ID" />
              <div className="space-y-2">
                <Label>Status</Label>
                <div className="flex items-center gap-2">
                  {gatewayEnabled ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span className="text-green-600 dark:text-green-400">Enabled and Available</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 text-gray-400" />
                      <span className="text-gray-500">Disabled or Unavailable</span>
                    </>
                  )}
                </div>
              </div>
              {gatewayConfig && (
                <>
                  <div className="space-y-2">
                    <Label>Merchant Name</Label>
                    <Input value={gatewayConfig.merchantName || gatewayConfig.displayName || ''} disabled />
                  </div>
                  <div className="grid gap-6 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Sandbox Mode</Label>
                      <Input value={(gatewayConfig.sandboxMode ?? gatewayConfig.testMode) ? 'Yes' : 'No'} disabled />
                    </div>
                    <div className="space-y-2">
                      <Label>Webhook URL</Label>
                      <Input value={gatewayConfig.webhookUrl || 'N/A'} disabled />
                    </div>
                  </div>
                  {gatewayConfig.priority !== undefined && (
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Input value={gatewayConfig.priority.toString()} disabled />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Metrics Tab */}
        <TabsContent value="metrics" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Performance Metrics</CardTitle>
              <CardDescription>Gateway performance and transaction statistics</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {metrics ? (
                <>
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <div className="space-y-2">
                      <Label>Success Rate</Label>
                      <Input
                        value={formatPercentage(metrics.successRate)}
                        disabled
                        className="text-lg font-semibold"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Availability</Label>
                      <Input
                        value={`${metrics.availability}%`}
                        disabled
                        className="text-lg font-semibold"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Active Transactions</Label>
                      <Input
                        value={metrics.activeTransactions.toString()}
                        disabled
                        className="text-lg font-semibold"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Success Count</Label>
                      <Input
                        value={metrics.successCount.toString()}
                        disabled
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Failure Count</Label>
                      <Input
                        value={metrics.failureCount.toString()}
                        disabled
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Updated</Label>
                      <Input
                        value={formatDate(metrics.timestamp)}
                        disabled
                      />
                    </div>
                  </div>
                  {Object.keys(metrics.transactionVolumes).length > 0 && (
                    <div className="space-y-2">
                      <Label>Transaction Volumes</Label>
                      <div className="space-y-2">
                        {Object.entries(metrics.transactionVolumes).map(([key, value]) => (
                          <div key={key} className="grid gap-4 md:grid-cols-2">
                            <Input value={key} disabled className="font-mono" />
                            <Input value={value.toString()} disabled />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No metrics data available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cache Tab */}
        <TabsContent value="cache" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Cache Status</CardTitle>
              <CardDescription>Gateway cache information and management</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {cacheStatus ? (
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Cache Status</Label>
                    <Input
                      value={cacheStatus.cached ? 'Cached' : 'Not Cached'}
                      disabled
                    />
                  </div>
                  {cacheStatus.lastRefresh && (
                    <div className="space-y-2">
                      <Label>Last Refresh</Label>
                      <Input
                        value={formatDate(cacheStatus.lastRefresh)}
                        disabled
                      />
                    </div>
                  )}
                  {cacheStatus.cacheSize !== undefined && (
                    <div className="space-y-2">
                      <Label>Cache Size</Label>
                      <Input
                        value={cacheStatus.cacheSize.toString()}
                        disabled
                      />
                    </div>
                  )}
                  {cacheStatus.ttl !== undefined && (
                    <div className="space-y-2">
                      <Label>TTL (Time To Live)</Label>
                      <Input
                        value={`${cacheStatus.ttl} seconds`}
                        disabled
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No cache status available
                </div>
              )}
              {canRefresh && (
                <div className="flex justify-end">
                  <Button onClick={handleRefreshCache} disabled={refreshCacheMutation.isPending}>
                    <RefreshCw className={`mr-2 h-4 w-4 ${refreshCacheMutation.isPending ? 'animate-spin' : ''}`} />
                    Refresh Cache
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PaymentGatewayDetailPage;
